import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { RESTRICTION_CATEGORIES, RestrictionCategory } from '../../screening-brief.constants';
import { ScreeningNogoList } from '../screening-nogo-list/screening-nogo-list';

/**
 * Wizard step 3 — "Browse & build": a category rail on the left, the active category's question and
 * pickable rules on the right, and the shared no-go list underneath.
 *
 * The search box filters both levels at once: a category stays visible if its own label matches, or
 * if any of its rules do.
 */
@Component({
  selector: 'sbs-screening-red-flags',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScreeningNogoList],
  templateUrl: './screening-red-flags.html',
})
export class ScreeningRedFlags {

  readonly createScreeningService = inject(CreateScreeningService);

  readonly query = signal<string>('');

  /** Category the user last clicked. Purely presentational, so it is not on the draft service. */
  private readonly requestedCategory = signal<string>(RESTRICTION_CATEGORIES[0].id);

  private readonly normalisedQuery = computed(() => this.query().trim().toLowerCase());

  readonly visibleCategories = computed<RestrictionCategory[]>(() => {
    const query = this.normalisedQuery();
    if (!query) {
      return RESTRICTION_CATEGORIES;
    }
    return RESTRICTION_CATEGORIES.filter(
      (category) =>
        category.label.toLowerCase().includes(query) ||
        category.enrichments.some((enrichment) => enrichment.toLowerCase().includes(query)),
    );
  });

  /** Falls back to the first visible category when a search hides the requested one. */
  readonly activeCategory = computed<RestrictionCategory | undefined>(() => {
    const visible = this.visibleCategories();
    return visible.find((category) => category.id === this.requestedCategory()) ?? visible[0];
  });

  /** Rules shown in the panel. A category-label match keeps all of its rules visible. */
  readonly visibleEnrichments = computed<string[]>(() => {
    const category = this.activeCategory();
    if (!category) {
      return [];
    }
    const query = this.normalisedQuery();
    if (!query || category.label.toLowerCase().includes(query)) {
      return [...category.enrichments];
    }
    return category.enrichments.filter((enrichment) => enrichment.toLowerCase().includes(query));
  });

  readonly matchCount = computed(() => this.visibleCategories().length);

  setQuery(value: string): void {
    this.query.set(value);
  }

  clearQuery(): void {
    this.query.set('');
  }

  selectCategory(id: string): void {
    this.requestedCategory.set(id);
  }

  countFor(categoryId: string): number {
    return this.createScreeningService.restrictionCounts()[categoryId] ?? 0;
  }

  isOn(categoryId: string, enrichment: string): boolean {
    return this.createScreeningService.hasRestriction(categoryId, enrichment);
  }

  toggle(category: RestrictionCategory, enrichment: string): void {
    this.createScreeningService.toggleRestriction(category.id, category.label, enrichment);
  }
}
